// 下载执行：合并有效筛选器与特殊维度全量，按指标生成表格数据并合并，最终调用 downloadExcel 导出
import { useCallback } from 'react';
import { uniqBy } from 'lodash-es';
import { IDownloadConfig, IDownloadContext, TSelectedFilters, TSelectedMetrics } from '../types';
import { UnionMergeStrategy } from '../utils/merger';
import { getDownloadTableData } from '../utils/dataGenerator';
import { buildAdditionalColumns } from '../utils/tableHelpers';
import { downloadExcel } from '@/pages/dashboard/utils/download';

/**
 * 下载执行 Hook
 */
export const useDownloadExecution = (props: {
  config: IDownloadConfig;
  context: IDownloadContext;
}) => {
  const { config, context } = props;

  // 合并策略
  const mergeStrategy = useCallback(() => {
    if (config.mergeStrategy === 'union') {
      return new UnionMergeStrategy();
    }
    // 可以扩展其他策略
    return new UnionMergeStrategy();
  }, [config.mergeStrategy])();

  /**
   * 执行下载
   * 直接使用多选的 selectedFilters 生成数据，不需要拆分组合
   */
  const execute = useCallback(
    async (
      selectedFilters: TSelectedFilters,
      selectedMetrics: TSelectedMetrics,
      downloadTitle?: string,
      tableInfo?: any
    ) => {
      try {
        // 1. 合并筛选器值：selectedFilters（用户在弹窗中选择的值）+ effectiveSelected.filters（默认选中的值）
        const effectiveFilters = context.effectiveSelected?.filters || {};
        const mergedFilters: TSelectedFilters = {
          ...effectiveFilters, // 先使用所有有效值（包括自动选中的单选项筛选器）
          ...selectedFilters, // 然后用用户在弹窗中选择的值覆盖
        };

        // 2. 处理特殊维度全量值
        config.specialDimensions.fullDownload.forEach((dimKey) => {
          const dim = context.dimensionInfo.find((d) => d.data_key === dimKey);
          if (dim) {
            mergedFilters[dimKey] = (dim.value || [])
              .map((val: any) => (typeof val === 'string' ? val : val?.value))
              .filter(Boolean) as string[];
          }
        });

        // 3. 如果没有选择指标，使用当前指标或有效指标
        let metricsToUse: string[] = [];
        if (selectedMetrics.length > 0) {
          metricsToUse = selectedMetrics;
        } else if (context.effectiveSelected?.metrics && context.effectiveSelected.metrics.length > 0) {
          metricsToUse = context.effectiveSelected.metrics;
        } else if (context.metricObj?.data_key) {
          metricsToUse = [context.metricObj.data_key];
        }

        // helper: columnsInfo -> download columns
        const buildColumns = (columnsInfo: any[] = []) =>
          columnsInfo.map((c) => ({
            code: c.origin_name,
            name: c.name,
            format: c.column_date_type || '',
          }));

        // 无指标则尝试用 tableInfo 直接导出
        if (metricsToUse.length === 0) {
          if (tableInfo?.columnsInfo) {
            const fileName = downloadTitle || 'download';
            
            // 合并 additional_table_field 的列到 tableInfo.columnsInfo
            let finalColumnsInfo = [...(tableInfo.columnsInfo || [])];
            if (context.additional_table_field && context.additional_table_field.length > 0) {
              const additionalColumns = buildAdditionalColumns(context.additional_table_field, 'download');
              // 合并并去重（基于 origin_name）
              finalColumnsInfo = uniqBy(
                [...finalColumnsInfo, ...additionalColumns],
                'origin_name'
              );
            }
            
            await downloadExcel({
              fileName,
              tabs: [
                {
                  tabName: fileName,
                  dataSource: tableInfo.data || [],
                  columns: buildColumns(finalColumnsInfo),
                },
              ],
            });
          }
          return;
        }

        // 4. 为每个指标生成数据
        const tableDataList = await Promise.all(
          metricsToUse.map((metric) => {
            try {
              const metricInfo = context.metricsInfo.find((m) => m.data_key === metric);
              const metricName = metricInfo?.name || metric;

              // 将 TSelectedFilters 转换为 Record<string, any>（支持数组）
              const filterInfoVal: Record<string, any> = {};
              Object.entries(mergedFilters).forEach(([key, values]) => {
                // 排除特殊维度（这些维度应该下载全量，不需要过滤）
                if (config.specialDimensions.fullDownload.includes(key)) {
                  return; // 跳过特殊维度
                }
                
                if (values && values.length > 0) {
                  // 如果只有一个值，使用单个值；如果有多个值，使用数组
                  filterInfoVal[key] = values.length === 1 ? values[0] : values;
                } else if (context.filterVal?.[key]) {
                  // 如果 selectedFilters 中没有该筛选器，使用 context.filterVal 中的值
                  filterInfoVal[key] = context.filterVal[key];
                }
              });
              const chartComData = getDownloadTableData({
                inputData: context.rawData,
                filterInfoVal,
                xAxis: context.xAxis,
                inputMetricsInfo: context.metricsInfo,
                inputLegends: context.legends,
                dimensionInfo: context.dimensionInfo,
                metric,
                additional_table_field: context.additional_table_field,
              });

              if (!chartComData?.columnsInfo || chartComData.columnsInfo.length === 0) {
                return null;
              }

              // 即使数据为空，也返回列信息，以便下载空文件（只有表头）
              return {
                data: chartComData.data || [],
                columnsInfo: chartComData.columnsInfo,
                metricName,
                metricKey: metric,
              };
            } catch (error) {
              console.error(`下载指标 ${metric} 时出错:`, error);
              return null;
            }
          })
        );

        // 5. 过滤掉 null
        const validTableDataList = tableDataList.filter((data) => data !== null) as any[];

        // 6. 如果只有一个指标，直接下载（即使 data 为空也导出表头）
        if (validTableDataList.length === 1) {
          const fileName = downloadTitle || 'download';
          const single = validTableDataList[0];
          await downloadExcel({
            fileName,
            tabs: [
              {
                tabName: 'file',
                dataSource: single.data || [],
                columns: buildColumns(single.columnsInfo),
              },
            ],
          });
          return;
        }

        // 7. 多个指标，合并数据
        const mergedData = mergeStrategy.merge(validTableDataList, {
          xAxis: context.xAxis,
          metricsInfo: context.metricsInfo,
          filterInfo: context.filterInfo,
          showFilters: context.showFilters,
          showMetrics: context.showMetrics,
        });

        // 8. 执行下载
        if (mergedData) {
          const fileName = downloadTitle || 'download';
          await downloadExcel({
            fileName,
            tabs: [
              {
                tabName: 'file',
                dataSource: mergedData?.data || [],
                columns: buildColumns(mergedData?.columnsInfo || []),
              },
            ],
          });
          return;
        }

        // 没有合并结果，尝试只导出表头（从第一个指标的 columnsInfo 获取）
        if (validTableDataList.length > 0) {
          const fileName = downloadTitle || 'download';
          const cols = buildColumns(validTableDataList[0].columnsInfo);
          await downloadExcel({
            fileName,
            tabs: [
              {
                tabName: 'file',
                dataSource: [],
                columns: cols,
              },
            ],
          });
          return;
        }

        // 如果完全没有可用列信息，尝试用当前 tableInfo 兜底
        if (tableInfo?.columnsInfo) {
          const fileName = downloadTitle || 'download';
          
          // 合并 additional_table_field 的列到 tableInfo.columnsInfo
          let finalColumnsInfo = [...(tableInfo.columnsInfo || [])];
          if (context.additional_table_field && context.additional_table_field.length > 0) {
            const additionalColumns = buildAdditionalColumns(context.additional_table_field, 'download');
            // 合并并去重（基于 origin_name）
            finalColumnsInfo = uniqBy(
              [...finalColumnsInfo, ...additionalColumns],
              'origin_name'
            );
          }
          
          await downloadExcel({
            fileName,
            tabs: [
              {
                tabName: 'file',
                dataSource: tableInfo.data || [],
                columns: buildColumns(finalColumnsInfo),
              },
            ],
          });
        }
      } catch (error) {
        console.error('下载数据时出错:', error);
        // 错误已经被捕获，不会导致白屏
        // 可以根据需要添加用户提示
      }
    },
    [config, context, mergeStrategy]
  );

  return {
    execute,
  };
};

